import { useContext, useEffect, useState, useRef } from "react"
import { AppContext } from "../App"
import { callApi } from "../Helpers";
import { getInlineLoader, CDN_URL } from "../Helpers";
import { MdBadge, MdInfo, MdLocationOn, MdPerson, MdPhoneIphone, MdDelete, MdPictureAsPdf } from "react-icons/md";
import { MdOutlinePerson, MdOutlinePhone, MdOutlineDirectionsBike, MdOutlineLocationOn, MdFileUpload, MdCheckCircle } from "react-icons/md";
import { IoMdInformation } from "react-icons/io";

export default function View({ uuid }) {

    const appContext = useContext(AppContext);

    const [ ready, setReady ] = useState(false);
    const [loading, setLoading] = useState(false);

    const [fullname, setFullname] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [nationalID, setNationalID] = useState('');
    const [tinNumber, setTinNumber] = useState('');
    const [drivingLicenseNumber, setDrivingLicenseNumber] = useState('');
    const [data, setData] = useState(null);
    
    // Dynamic Guarantors State (Max 2)
    const [guarantors, setGuarantors] = useState([]);
    
    // Preview states (Used ONLY to display images on screen)
    const [tinPreview, setTinPreview] = useState(null);
    const [drivingLicensePreview, setDrivingLicensePreview] = useState(null);
    const [passportPreview, setPassportPreview] = useState(null);

    // Binary File Cache - Initialized to null to match pure multipart form expectations
    const [tinFile, setTinFile] = useState(null);
    const [drivingLicenseFile, setDrivingLicenseFile] = useState(null);
    const [passportFile, setPassportFile] = useState(null);

    const tinRef = useRef(null);
    const drivingLicenseRef = useRef(null);
    const passportRef = useRef(null);

    const isValidPhoneNumber = (value) => /^0\d{9}$/.test((value || "").trim());

    const handleFileChange = (e, previewSetter, fileSetter) => {
        const file = e.target?.files?.[0];
        if (file) {
            fileSetter(file);
            const reader = new FileReader();
            reader.onload = (event) => previewSetter(event.target.result);
            reader.readAsDataURL(file);
        }
    };

    // --- Dynamic Guarantor Handlers ---
    const handleAddGuarantor = () => {
        if (guarantors.length < 2) {
            setGuarantors([...guarantors, { id: Date.now(), fullname: '', phone: '', passportFile: null, passportPreview: null }]);
        }
    };

    const handleRemoveGuarantor = (id) => {
        setGuarantors(guarantors.filter(g => g.id !== id));
    };

    const updateGuarantorField = (id, field, value) => {
        setGuarantors(guarantors.map(g => g.id === id ? { ...g, [field]: value } : g));
    };

    const handleGuarantorFileChange = (e, id) => {
        const file = e.target?.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setGuarantors(prevGuarantors => prevGuarantors.map(g => 
                    g.id === id ? { ...g, passportFile: file, passportPreview: event.target.result } : g
                ));
            };
            reader.readAsDataURL(file);
        }
    };
    // -----------------------------------

    const renderFilePreview = (url) => {
        if (!url) return <div className="text-muted small p-4 text-center border rounded-3 bg-light opacity-50">No file uploaded</div>;
        
        const isImage = url.match(/\.(jpeg|jpg|png|gif|webp)$/i) || url.startsWith("data:image");
        const isPdf = url.match(/\.(pdf)$/i) || url.startsWith("data:application/pdf");

        return (
            <div className="mt-2 position-relative shadow-sm rounded-3 overflow-hidden border">
                {isImage ? (
                    <img src={url} alt="preview" className="img-fluid d-block" style={{ maxHeight: "150px", width: "100%", objectFit: "cover" }} />
                ) : isPdf ? (
                    <div style={{ height: "150px", width: "100%" }} className="bg-light d-flex flex-column align-items-center justify-content-center">
                        <object data={url} type="application/pdf" width="100%" height="100%" className="d-block">
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 p-3">
                                <MdPictureAsPdf size={48} className="text-danger mb-2" />
                                <span className="small fw-bold text-muted">PDF Attached</span>
                            </div>
                        </object>
                    </div>
                ) : (
                    <div className="p-3 bg-white d-flex align-items-center gap-2">
                        <MdInfo className="text-danger" /> <span className="small fw-bold">Unrecognized Format</span>
                    </div>
                )}
            </div>
        );
    };

    async function init() {
        setReady(false);
        await Promise.all([
            appContext.getAllCustomers(),
            (async () => {
                if(uuid) {
                    await getData();
                }
            })(),
        ]);
        setReady(true);
    }

    async function getData() {
        await callApi('get-customer-data', { uuid }).then((response) => {
            if(response.status === 1) {
                setData(response.data)
            } else {
                appContext.tellError('Could not fetch data');
            }
        });
    }

    async function create() {
        if(fullname && fullname.trim().length > 0) {
            if(phone && phone.trim().length > 0 && isValidPhoneNumber(phone)) {
                
                // Flexible Validation: Requires either TIN or Driving License (Number OR File OR Existing Preview)
                const hasTin = (tinNumber && tinNumber.trim().length > 0) || tinFile || tinPreview;
                const hasDrivingLicense = (drivingLicenseNumber && drivingLicenseNumber.trim().length > 0) || drivingLicenseFile || drivingLicensePreview;

                if(hasTin || hasDrivingLicense) {
                    const invalidGuarantorPhone = guarantors.find(g => g.phone && g.phone.trim().length > 0 && !isValidPhoneNumber(g.phone));
                    if (invalidGuarantorPhone) {
                        appContext.tellError("Guarantor phone numbers must start with 0 and be exactly 10 digits");
                        return;
                    }

                    setLoading(true);
                    
                    const validGuarantors = guarantors.filter(g => 
                        (g.fullname && g.fullname.trim().length > 0) || 
                        (g.phone && g.phone.trim().length > 0) || 
                        g.passportFile !== null
                    );
                    
                    await callApi(
                        'create-customer',
                        {
                            fullname,
                            phone,
                            address,
                            nationalID,
                            tinNumber,
                            drivingLicenseNumber,
                            guarantors: validGuarantors, 
                            
                            drivingLicense: drivingLicenseFile,
                            passport: passportFile,
                            tin: tinFile,
                        }
                    ).then(response => {
                        if(response.status === 1) {
                            appContext.tellMessage('Successful');
                            appContext.navBack();
                            appContext.getAllCustomers();
                        } else {
                            appContext.tellError(response.msg);
                        }
                    });
                    setLoading(false);
                } else {
                    appContext.tellError("Please provide either a TIN or Driving License (Number or Attachment)");
                }
            } else {
                appContext.tellError("Customer phone numbers must start with 0 and be exactly 10 digits");
            }
        } else {
            appContext.tellError("Invalid Customer Name");
        }
    }

    async function update() {
        if(fullname && fullname.trim().length > 0) {
            if(phone && phone.trim().length > 0 && isValidPhoneNumber(phone)) {
                
                // Flexible Validation: Requires either TIN or Driving License (Number OR File OR Existing Preview)
                const hasTin = (tinNumber && tinNumber.trim().length > 0) || tinFile || tinPreview;
                const hasDrivingLicense = (drivingLicenseNumber && drivingLicenseNumber.trim().length > 0) || drivingLicenseFile || drivingLicensePreview;

                if(hasTin || hasDrivingLicense) {
                    const invalidGuarantorPhone = guarantors.find(g => g.phone && g.phone.trim().length > 0 && !isValidPhoneNumber(g.phone));
                    if (invalidGuarantorPhone) {
                        appContext.tellError("Guarantor phone numbers must start with 0 and be exactly 10 digits");
                        return;
                    }

                    setLoading(true);

                    const validGuarantors = guarantors.filter(g => 
                        (g.fullname && g.fullname.trim().length > 0) || 
                        (g.phone && g.phone.trim().length > 0) || 
                        g.passportFile !== null || 
                        g.passportPreview !== null
                    );

                    await callApi(
                        'update-customer',
                        {
                            uuid,
                            fullname,
                            phone,
                            address,
                            nationalID,
                            tinNumber,
                            drivingLicenseNumber,
                            guarantors: validGuarantors, 
                            
                            drivingLicense: drivingLicenseFile,
                            passport: passportFile,
                            tin: tinFile,
                        }
                    ).then(response => {
                        if(response.status === 1) {
                            appContext.tellMessage('Successful');
                            appContext.navBack();
                            appContext.getAllCustomers();
                        } else {
                            appContext.tellError(response.msg);
                        }
                    });
                    setLoading(false);
                } else {
                    appContext.tellError("Please provide either a TIN or Driving License (Number or Attachment)");
                }
            } else {
                appContext.tellError("Customer phone numbers must start with 0 and be exactly 10 digits");
            }
        } else {
            appContext.tellError("Invalid Customer Fullname");
        }
    }

    useEffect(() => {
        init();
    }, [ uuid ]);

    useEffect(() => {
        if (data) {
            setFullname(data.fullname || '');
            setPhone(data.phone || '');
            setAddress(data.address || '');
            setNationalID(data.nationalID || '');
            setTinNumber(data.tinNumber || '');
            setDrivingLicenseNumber(data.drivingLicenseNumber || '');
            
            // Map image path URLs from CDN to preview thumbnails
            setDrivingLicensePreview(data.drivingLicense ? CDN_URL + data.drivingLicense : null);
            setPassportPreview(data.passport ? CDN_URL + data.passport : null);
            setTinPreview(data.tin ? CDN_URL + data.tin : null);
            
            // Map guarantors if available from the backend
            if (data.guarantors && Array.isArray(data.guarantors)) {
                const mappedGuarantors = data.guarantors.map(g => ({
                    id: g.id || Date.now() + Math.random(),
                    fullname: g.fullname || '',
                    phone: g.phone || '',
                    passportPreview: g.passport ? CDN_URL + g.passport : null,
                    passportFile: null
                }));
                setGuarantors(mappedGuarantors);
            } else if (data.guarantorFullname) {
                // Fallback for legacy data before migrating to array structure
                setGuarantors([{
                    id: Date.now(),
                    fullname: data.guarantorFullname || '',
                    phone: data.guarantorPhone || '',
                    passportPreview: data.guarantorPassport ? CDN_URL + data.guarantorPassport : null,
                    passportFile: null
                }]);
            }
        }
    }, [data]);

    useEffect(() => {
        appContext.setShowOverlayLoader(loading);
        return () => {
            appContext.setShowOverlayLoader(false);
        }
    }, [ loading ]);

    if(ready) {
        return (
            <div className="container">
                <div className="app-form-page">
                    <div className="app-form-shell">
                        <div className="app-form-header">
                            <div>
                                <h6 className="app-form-title">
                                    {(uuid) ? "Edit Customer" : "Add Customer"}
                                </h6>
                            </div>
                            <div className="app-form-badge">
                                <MdPerson size={18} />
                                {uuid ? "Updating record" : "New record"}
                            </div>
                        </div>

                        <div className="app-form-section">
                            <div className="row">
                                <h3 className="font-bold">Customer Information</h3>
                                <div className="col-md-6 mt-3 mb-3">
                                    <div className="app-form-input-wrap">
                                        <label className="form-label">Fullname</label>
                                        <input
                                            className="form-control"
                                            value={fullname}
                                            onChange={(e) => setFullname(e.target.value)}
                                            placeholder="Enter customer fullname"
                                        />
                                        <div className="app-form-helper">
                                            <MdPerson size={14} /> Use the customer&apos;s official or most-used name.
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6 mt-3 mb-3">
                                    <div className="app-form-input-wrap">
                                        <label className="form-label">Phone</label>
                                        <input
                                            className="form-control"
                                            type="tel"
                                            inputMode="numeric"
                                            maxLength={10}
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="e.g. 0712345678"
                                        />
                                        <div className="app-form-helper">
                                            <MdPhoneIphone size={14} /> This is used for customer follow-up and repayment contact.
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6 mt-3 mb-3">
                                    <div className="app-form-input-wrap">
                                        <label className="form-label">Address</label>
                                        <input
                                            className="form-control"
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            placeholder="Enter address"
                                        />
                                        <div className="app-form-helper">
                                            <MdLocationOn size={14} /> A short address makes future visits and verification easier.
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6 mt-3 mb-3">
                                    <div className="app-form-input-wrap">
                                        <label className="form-label">National Identification Number</label>
                                        <input
                                            className="form-control"
                                            value={nationalID}
                                            onChange={(e) => setNationalID(e.target.value)}
                                            placeholder="Enter ID number"
                                        />
                                        <div className="app-form-helper">
                                            <MdBadge size={14} /> Optional for stronger customer verification.
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4 mt-3 mb-3 d-flex">
                                    <div className="app-form-input-wrap h-100 d-flex flex-column w-100">
                                        <label className="form-label">TIN Certificate</label>
                                        <input
                                            className="form-control"
                                            value={tinNumber}
                                            onChange={(e) => setTinNumber(e.target.value)}
                                            placeholder="Enter TIN number"
                                        />
                                        {renderFilePreview(tinPreview)}
                                        <button className="btn btn-outline-primary btn-sm w-100 mt-3 d-flex align-items-center justify-content-center gap-2" onClick={() => tinRef.current.click()}>
                                            <MdFileUpload /> Upload TIN Certificate
                                        </button>
                                        <input type="file" hidden ref={tinRef} accept="image/*,application/pdf" onChange={(e) => handleFileChange(e, setTinPreview, setTinFile)} />
                                    </div>
                                </div>

                                <div className="col-md-4 mt-3 mb-3 d-flex">
                                    <div className="app-form-input-wrap h-100 d-flex flex-column w-100">
                                        <label className="form-label">Driving License</label>
                                        <input
                                            className="form-control"
                                            value={drivingLicenseNumber}
                                            onChange={(e) => setDrivingLicenseNumber(e.target.value)}
                                            placeholder="Enter driving license number"
                                        />
                                        {renderFilePreview(drivingLicensePreview)}
                                        <button className="btn btn-outline-primary btn-sm w-100 mt-3 d-flex align-items-center justify-content-center gap-2" onClick={() => drivingLicenseRef.current.click()}>
                                            <MdFileUpload /> Upload License
                                        </button>
                                        <input type="file" hidden ref={drivingLicenseRef} accept="image/*,application/pdf" onChange={(e) => handleFileChange(e, setDrivingLicensePreview, setDrivingLicenseFile)} />
                                    </div>
                                </div>

                                <div className="col-md-4 mt-3 mb-3 d-flex">
                                    <div className="app-form-input-wrap h-100 d-flex flex-column w-100">
                                        <label className="form-label">Passport</label>
                                        {renderFilePreview(passportPreview)}
                                        <button className="btn btn-outline-primary btn-sm w-100 mt-3 mt-auto d-flex align-items-center justify-content-center gap-2" onClick={() => passportRef.current.click()}>
                                            <MdFileUpload /> Upload Customer Passport
                                        </button>
                                        <input type="file" hidden ref={passportRef} accept="image/*" onChange={(e) => handleFileChange(e, setPassportPreview, setPassportFile)} />
                                    </div>
                                </div>

                                <h3 className="font-bold mt-4 mb-2 border-bottom pb-2 d-flex justify-content-between align-items-center">
                                    Guarantor Details
                                    {guarantors.length < 2 && (
                                        <button className="btn btn-sm btn-outline-primary rounded-pill" onClick={handleAddGuarantor}>
                                            + Add Guarantor
                                        </button>
                                    )}
                                </h3>
                                
                                {guarantors.length === 0 && (
                                    <div className="col-12 text-muted text-center py-4 bg-light rounded-3 border mb-3">
                                        No guarantors added. Click <b className="text-primary">+ Add Guarantor</b>
                                    </div>
                                )}

                                {guarantors.map((g, index) => (
                                    <div key={g.id} className="row align-items-start bg-light rounded-3 border p-3 mb-4 mx-0 position-relative">
                                        <div className="position-absolute top-0 end-0 p-2" style={{ width: 'auto' }}>
                                            <button className="btn btn-sm btn-danger rounded-circle" onClick={() => handleRemoveGuarantor(g.id)} title="Remove Guarantor">
                                                <MdDelete />
                                            </button>
                                        </div>
                                        
                                        <div className="col-12 fw-bold text-primary mb-2">Guarantor {index + 1}</div>

                                        <div className="col-md-4 mb-3">
                                            <div className="app-form-input-wrap h-100">
                                                <label className="form-label">Full Name</label>
                                                <input
                                                    className="form-control"
                                                    value={g.fullname}
                                                    onChange={(e) => updateGuarantorField(g.id, 'fullname', e.target.value)}
                                                    placeholder="Enter FullNames"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <div className="app-form-input-wrap h-100">
                                                <label className="form-label">Phone Number</label>
                                                <input
                                                    className="form-control"
                                                    type="tel"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    value={g.phone}
                                                    onChange={(e) => updateGuarantorField(g.id, 'phone', e.target.value)}
                                                    placeholder="e.g. 0712345678"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <div className="app-form-input-wrap h-100">
                                                <label className="form-label">Passport</label>
                                                {renderFilePreview(g.passportPreview)}
                                                
                                                {/* Using htmlFor trick to avoid mapping dynamic refs */}
                                                <label htmlFor={`guarantor-file-${g.id}`} className="btn btn-outline-primary btn-sm w-100 mt-3 d-flex align-items-center justify-content-center gap-2 cursor-pointer">
                                                    <MdFileUpload /> Upload Document
                                                </label>
                                                <input 
                                                    id={`guarantor-file-${g.id}`} 
                                                    type="file" 
                                                    hidden 
                                                    accept="image/*" 
                                                    onChange={(e) => handleGuarantorFileChange(e, g.id)} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="col-12 mt-3 mb-3 text-end app-form-submit-row">
                                    <button
                                        className="btn btn-padded btn-rounded btn-primary btn-block app-form-submit-btn"
                                        onClick={(uuid) ? update : create}
                                    >
                                        SUBMIT
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    } else {
        return (
            <div className="container mSupportLoading">
                {getInlineLoader()}
            </div>
        );
    }
}
